// Location permission observer. Polls @capacitor/geolocation checkPermissions
// (with a web fallback to navigator.permissions) and re-checks on app
// foreground so the UI updates after the user changes Settings.
//
// We deliberately return only a 4-value enum — the OfflineTrackingStatus
// panel maps "always" vs "whenInUse" to a single "granted" because either is
// enough to start tracking. The /tracking screen already has its own
// always-vs-whenInUse nag (LocationBG.getAuthStatus).
import { useEffect, useState } from "react";
import { Geolocation } from "@capacitor/geolocation";
import { isNative } from "./platform";

export type LocationPermission = "granted" | "denied" | "prompt" | "unknown";

export async function getLocationPermission(): Promise<LocationPermission> {
  try {
    if (isNative()) {
      const res = await Geolocation.checkPermissions();
      const v = res.location;
      if (v === "granted") return "granted";
      if (v === "denied") return "denied";
      if (v === "prompt" || v === "prompt-with-rationale") return "prompt";
      return "unknown";
    }
    // Web: navigator.permissions is the only reliable source.
    if (typeof navigator !== "undefined" && (navigator as any).permissions?.query) {
      try {
        const p = await (navigator as any).permissions.query({ name: "geolocation" });
        if (p.state === "granted") return "granted";
        if (p.state === "denied") return "denied";
        if (p.state === "prompt") return "prompt";
      } catch { /* unsupported */ }
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/** Hook that polls every 10s + on foreground/focus. */
export function useLocationPermission(): LocationPermission {
  const [perm, setPerm] = useState<LocationPermission>("unknown");

  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      const v = await getLocationPermission();
      if (alive) setPerm(v);
    };
    void refresh();
    const interval = window.setInterval(refresh, 10_000);
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    const onFocus = () => { void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      alive = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return perm;
}

/** One-shot request; returns the resulting permission state. */
export async function requestLocationPermissionSimple(): Promise<LocationPermission> {
  try {
    const res = await Geolocation.requestPermissions();
    const v = res.location;
    if (v === "granted") return "granted";
    if (v === "denied") return "denied";
    if (v === "prompt" || v === "prompt-with-rationale") return "prompt";
    return "unknown";
  } catch {
    return "unknown";
  }
}
